import { UAParser } from 'ua-parser-js';
import { Environment } from './enums';
import http from '../../utils/request';

const DEFAULT_CALLBACK_URL = window.location.origin + '/zero/auth';
const API_URL = 'wss://ws.0account.com/ws';
const API_URL_STAGING = 'wss://staging.ws.0account.com/ws';

const PREFIX = '0account';
const SESSION_ID = 'session-id';
const SESSION_TOKEN = 'session-token';

const fromWidgetUUIDUpdated = 'from-widget-uuid-updated';
const fromWidgetLoggedOut = 'from-widget-logged-out';
// const fromWidgetAuthenticationStarted = 'from-widget-authentication-started';
const fromWidgetEnsureSession = 'from-widget-ensure-session';

const toWidgetLogout = 'to-widget-logout';
const toWidgetApproved = 'to-widget-approved';
const toAnyError = 'to-any-error';
// CustomEvents
const zeroLogout = '0account-logout';
const zeroAuthenticated = '0account-authenticated';
// const zeroAuthenticationFailed = '0account-authentication-failed';

function debounce(func, timeout = 300) {
  let timer;
  return () => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func();
    }, timeout);
  };
}

type Options = {
  callbackURL: string,
  updateInterval: number,
  enableWithCredentials: boolean,
  uaParser: UAParser,
  log: (...args) => void
  env?: Environment,
}


export class WS {
  private readonly appId: string;
  private socket: WebSocket;
  private uaParser: UAParser;
  private readonly log: (...args) => void;
  private uuid: string;
  private wsURL = API_URL;
  private callbackURL = DEFAULT_CALLBACK_URL;
  private request: XMLHttpRequest;
  private readonly updateInterval: number;
  private enableWithCredentials: boolean;

  constructor(appId: string, opts: Options) {
    if (!opts.env) opts.env = Environment.production

    this.appId = appId;
    this.uaParser = opts.uaParser;
    this.callbackURL = opts.callbackURL;
    this.updateInterval = opts.updateInterval;
    this.enableWithCredentials = opts.enableWithCredentials;
    this.log = opts.log;
    if (opts.env !== Environment.production) this.wsURL = API_URL_STAGING;
    this.init();
  }

  private deviceInfo() {
    return {
      appId: this.appId,
      browser: this.uaParser.getBrowser().withFeatureCheck().name,
      os: this.uaParser.getOS().withFeatureCheck().name,
      // TODO: handle these on backend:
      // device: `${this.uaParser.getDevice().vendor} ${this.uaParser.getDevice().model}`,
      // all: this.uaParser.getResult()
    };
  }

  private reinit() {
    const retry = debounce(this.init.bind(this), 1000)
    retry()
  }

  private init() {
    const onopen = (_) => {
      this.send(fromWidgetEnsureSession, this.deviceInfo());
    };
    const onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        this.handleMessage(data);
      } catch (e) {
        this.log(e);
      }
    };
    const onerror = (_) => {
      if (this.socket && (this.socket.readyState !== WebSocket.CLOSED
          && this.socket.readyState !== WebSocket.CLOSING)) {
        this.socket.close();
      }
    };
    const onclose = (_) => {
      if (this.socket) {
        this.socket.removeEventListener('open', onopen);
        this.socket.removeEventListener('message', onmessage);
        this.socket.removeEventListener('error', onerror);
        this.socket.removeEventListener('close', onclose);
        delete this.socket;
      }
      this.reinit();
    };

    if (this.socket) {
      this.socket.removeEventListener('open', onopen);
      this.socket.removeEventListener('message', onmessage);
      this.socket.removeEventListener('error', onerror);
      this.socket.removeEventListener('close', onclose);
    }
    this.socket = new WebSocket(this.wsURL);
    this.socket.addEventListener('open', onopen);
    this.socket.addEventListener('message', onmessage);
    this.socket.addEventListener('error', onerror);
    this.socket.addEventListener('close', onclose);
  }

  updateUUID(uuid: string) {
    this.uuid = uuid;
    this.send(fromWidgetUUIDUpdated, this.deviceInfo());
  }

  logout() {
    this.send(fromWidgetLoggedOut, this.deviceInfo());
  }

  send(action, data) {
    if (!this.socket || !this.socket.readyState || this.socket.readyState !== WebSocket.OPEN) {
      const retry = debounce(this.send.bind(this, action, data), 1000)
      return retry();
    }

    const msg: any = {
      action: action,
      timeout: `${this.updateInterval}ms`,
      uuid: this.uuid,
      appId: `${this.appId}`,
      message: {},
    };
    if (action === fromWidgetEnsureSession || action === fromWidgetLoggedOut) {
      const sessionId = localStorage.getItem(`${PREFIX}_${SESSION_ID}`);
      const sessionToken = localStorage.getItem(`${PREFIX}_${SESSION_TOKEN}`);
      if (!sessionId || !sessionToken) return;
      msg.sessionId = sessionId;
      msg.sessionToken = sessionToken;
    }

    if (data) msg.message = data;
    this.socket.send(JSON.stringify(msg));
  }

  onZeroAuth() {
    if (this.request) this.request.abort();
    this.request = http.send(this.callbackURL,
      {
        enableWithCredentials: this.enableWithCredentials,
        headers: {
          'x-0account-uuid': this.uuid,
          'x-0account-auth': 'true',
        },
      },
      (responseText) => {
        try {
          const data = JSON.parse(responseText);
          this.dispatchEventAndWsSend(zeroAuthenticated, data);
        } catch (err) {
          this.log(err);
        }
      },
    );
  }

  private handleMessage(msg: any) {
    switch (msg.action) {
      case toWidgetApproved:
        this.onZeroAuth();
        localStorage.setItem(`${PREFIX}_${SESSION_ID}`, msg.sessionId);
        localStorage.setItem(`${PREFIX}_${SESSION_TOKEN}`, msg.message);
        break;
      case toWidgetLogout:
        localStorage.removeItem(`${PREFIX}_${SESSION_ID}`);
        localStorage.removeItem(`${PREFIX}_${SESSION_TOKEN}`);
        this.dispatchEvent(zeroLogout);
        this.send(fromWidgetLoggedOut, this.deviceInfo());
        break;
      case toAnyError:
        this.log('ws error', msg);
        break;
      default:
        this.log('incorrect action', msg.action, msg);
    }
  }

  private dispatchEventAndWsSend(name, data) {
    this.dispatchEvent(name, data);
    this.send('from-widget-' + name, data);
  }

  private dispatchEvent(name: string, data?: any) {
    let event = new CustomEvent(name);
    if (data) event = new CustomEvent(name, { detail: data });
    document.dispatchEvent(event);
  }

}
